<template>
  <div class="flex h-screen bg-[#f3f4f8]">
    <!-- Sidebar Component -->
    <DelivererSidebar :isOpen="sidebarOpen" @close="sidebarOpen = false" />
    <!-- Overlay for mobile when sidebar open -->
    <div v-if="sidebarOpen" class="fixed inset-0 bg-black bg-opacity-40 z-20 md:hidden" @click="sidebarOpen = false">
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-auto relative z-10 transition-all duration-300">
      <!-- Header with sidebar toggle -->
      <header class="bg-white shadow-sm sticky top-0 z-30">
        <div class="px-6 py-4 flex items-center justify-between">
          <!-- Left: Burger + Text -->
          <div class="flex items-center space-x-4">
            <!-- Sidebar toggle button (burger) -->
            <button @click="sidebarOpen = !sidebarOpen" class="md:hidden text-[#4d148c] focus:outline-none">
              <i class="fas fa-bars text-2xl"></i>
            </button>

            <!-- Header Text -->
            <div class="flex flex-col">
              <span class="text-[#4d148c] font-medium text-sm md:text-base">Available Items</span>
              <h2 class="text-gray-500 text-sm text-gray-500 mt-0.5">Find items that match your travel route</h2>
            </div>
          </div>

          <!-- Optional right side (if you want user/profile) -->
          <div class="hidden md:flex items-center space-x-4">
            <!-- Example: User avatar or actions -->
          </div>
        </div>
      </header>


      <!-- Error Popup -->
      <div v-if="showErrorPopup"
        class="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg shadow-lg z-50 animate-slideInDown">
        <div class="flex items-center">
          <i class="fas fa-exclamation-circle mr-2"></i>
          {{ errorMessage }}
          <button @click="showErrorPopup = false" class="ml-4 text-red-400 hover:text-red-600 focus:outline-none">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="container mx-auto px-4 py-6">
        <!-- Filters Section -->
        <div class="bg-white rounded-xl shadow-sm mb-6">
          <!-- Filters Header -->
          <div class="p-4 border-b">
            <h3 class="text-lg font-medium text-gray-900">Filters</h3>
            <p class="mt-1 text-sm text-gray-500">Refine your search results</p>
          </div>

          <!-- Filters Content -->
          <div class="p-4">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <!-- Search -->
              <div class="col-span-1 md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fas fa-search text-gray-400"></i>
                  </div>
                  <input v-model="filters.search" type="text" placeholder="Search by item name or category..."
                    class="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#4d148c] focus:border-transparent transition-colors duration-200"
                    @input="applyFilters">
                </div>
              </div>

              <!-- Items Per Page -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Items per page</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fas fa-list-ol text-gray-400"></i>
                  </div>
                  <select v-model="filters.per_page"
                    class="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#4d148c] focus:border-transparent appearance-none transition-colors duration-200"
                    @change="applyFilters">
                    <option :value="10">10 items</option>
                    <option :value="20">20 items</option>
                    <option :value="50">50 items</option>
                    <option :value="100">100 items</option>
                  </select>
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <i class="fas fa-chevron-down text-gray-400"></i>
                  </div>
                </div>
              </div>

              <!-- Radius Filter -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">Search Items Near You (Radius in km)</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fas fa-map-marker-alt text-gray-400"></i>
                  </div>
                  <input type="number" v-model="radius_km" min="1" placeholder="Enter radius in kilometers"
                    class="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#4d148c] focus:border-transparent transition-colors duration-200" />
                </div>
              </div>

            </div>
            <div class="bg-white rounded-xl shadow-sm p-4 sm:p-6 space-y-6 animate-fadeInUp animation-delay-200">
              <!-- Heading -->
              <h3 class="text-lg sm:text-xl font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                <i class="fas fa-map-marker-alt text-[#4d148c]"></i>
                <span>Pickup & Drop-off Locations</span>
              </h3>

              <!-- Grid Layout -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">

                <!-- Origin Location -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Origin Location (Your Current Location) <span class="text-red-500">*</span>
                  </label>

                  <!-- Button or Info -->
                  <div v-if="!origin_address">
                    <button @click="showOriginMap = true"
                      class="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-[#4d148c] hover:bg-[#6d2ba8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4d148c]">
                      <i class="fas fa-map-marker-alt mr-2"></i>
                      Select Location
                    </button>
                  </div>

                  <div v-else class="flex items-center justify-between p-3 bg-purple-50 rounded-lg mt-2">
                    <div class="flex items-center space-x-2 text-sm sm:text-base">
                      <i class="fas fa-map-marker-alt text-[#4d148c]"></i>
                      <span class="text-gray-700">Origin location selected</span>
                    </div>
                    <button @click="showOriginMap = true" class="text-[#4d148c] hover:text-[#6d2ba8]">
                      <i class="fas fa-edit"></i>
                    </button>
                  </div>

                  <!-- Map for origin -->
                  <div v-if="showOriginMap" class="mt-3">
                    <div class="flex justify-between items-center mb-2">
                      <h4 class="text-sm font-medium text-gray-700">Select location on map</h4>
                      <button @click="showOriginMap = false" class="text-gray-400 hover:text-gray-500">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                    <MapLocation @update:location="getOriginLocation" v-show="!showConsent && !showRequestModal" />
                  </div>
                </div>

                <!-- Destination Location -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Destination Location <span class="text-red-500">*</span>
                  </label>

                  <!-- Button or Info -->
                  <div v-if="!destination_address">
                    <button @click="showMap = true"
                      class="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-[#4d148c] hover:bg-[#6d2ba8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4d148c]">
                      <i class="fas fa-map-marker-alt mr-2"></i>
                      Select Location
                    </button>
                  </div>

                  <div v-else class="flex items-center justify-between p-3 bg-purple-50 rounded-lg mt-2">
                    <div class="flex items-center space-x-2 text-sm sm:text-base">
                      <i class="fas fa-map-marker-alt text-[#4d148c]"></i>
                      <span class="text-gray-700">Destination location selected</span>
                    </div>
                    <button @click="showMap = true" class="text-[#4d148c] hover:text-[#6d2ba8]">
                      <i class="fas fa-edit"></i>
                    </button>
                  </div>

                  <!-- Map for destination -->
                  <div v-if="showMap" class="mt-3">
                    <div class="flex justify-between items-center mb-2">
                      <h4 class="text-sm font-medium text-gray-700">Select location on map</h4>
                      <button @click="showMap = false" class="text-gray-400 hover:text-gray-500 text-lg">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                    <MapLocation @update:location="getDestinationLocation" v-show="!showConsent && !showRequestModal" />
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>

        <!-- Results Section -->
        <div v-if="!destination_address" class="bg-white rounded-xl shadow-sm p-8 text-center">
          <div class="inline-block p-4 rounded-full bg-purple-100 mb-4">
            <i class="fas fa-map-marker-alt text-4xl text-[#4d148c]"></i>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">Select Destination and Current Location</h3>
          <p class="text-gray-500">Please select a destination and current location to view available items</p>
        </div>

        <!-- Loading State -->
        <div v-else-if="loading" class="bg-white rounded-xl shadow-sm p-8 text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-4 border-[#4d148c] border-t-transparent mx-auto">
          </div>
          <p class="mt-4 text-gray-600">Loading available items...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
          <div class="flex items-center">
            <i class="fas fa-exclamation-circle mr-2"></i>
            {{ error }}
          </div>
        </div>

        <!-- Items Grid -->
        <div v-else>
          <!-- Results Header -->
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-lg font-medium text-gray-900">
              Available Items
              <span class="text-sm text-gray-500 ml-2">
                (Showing {{ items.length }} of {{ pagination.total }} items)
              </span>
            </h2>
          </div>

          <!-- Items Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div v-for="item in items" :key="item.id"
              class="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden animate-cardSlideUp">
              <!-- Item Details -->
              <div class="p-6">
                <div class="flex justify-between items-start mb-4">
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900">{{ item.name }}</h3>
                  </div>
                  <span class="px-3 py-1 text-xs font-medium rounded-full" :class="getStatusClass(item.status)">
                    {{ item.status }}
                  </span>
                </div>

                <div class="space-y-3">
                  <!-- Category -->
                  <div class="flex items-start">
                    <i class="fas fa-tag w-5 text-[#4d148c]"></i>
                    <div class="ml-2">
                      <p class="text-sm text-gray-600 capitalize">
                        Category: {{ item.category }}
                      </p>
                    </div>
                  </div>

                  <!-- Location -->
                  <div class="flex items-start">
                    <i class="fas fa-map-marker-alt w-5 text-[#4d148c]"></i>
                    <div class="ml-2">
                      <p class="text-sm text-gray-600">From: {{ item.from_location }}</p>
                      <p class="text-sm text-gray-600">To: {{ item.to_location }}</p>
                    </div>
                  </div>

                  <!-- Deadline -->
                  <div class="flex items-start">
                    <i class="fas fa-calendar w-5 text-[#4d148c]"></i>
                    <div class="ml-2">
                      <p class="text-sm text-gray-600">
                        Delivery by: {{ formatDate(item.delivery_deadline) }}
                      </p>
                    </div>
                  </div>

                  <!-- Details -->
                  <div class="flex items-start">
                    <i class="fas fa-box w-5 text-[#4d148c]"></i>
                    <div class="ml-2">
                      <p class="text-sm text-gray-600">
                        Quantity: {{ item.quantity }} • Weight: {{ item.weight }}kg
                        <template v-if="item.is_fragile">
                          • <span class="text-amber-600">Fragile</span>
                        </template>
                      </p>
                    </div>
                  </div>

                  <!-- Dimensions -->
                  <div class="flex items-start">
                    <i class="fas fa-ruler-combined w-5 text-[#4d148c]"></i>
                    <div class="ml-2">
                      <p class="text-sm text-gray-600">
                        {{ item.dimensions.length }}x{{ item.dimensions.width }}x{{ item.dimensions.height }} cm
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Buttons -->
                <div class="mt-6 flex gap-3">
                  <!-- View Details Button -->
                  <router-link :to="`/available-items/${item.id}`"
                    class="flex-1 inline-flex justify-center items-center px-4 py-2.5 border border-[#4d148c] text-sm font-medium rounded-lg text-[#4d148c] hover:bg-[#4d148c] hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4d148c] transition-colors duration-200">
                    <i class="fas fa-eye mr-2"></i>
                    View Details
                  </router-link>

                  <!-- Request Delivery Button -->
                  <button @click="openRequestModal(item)"
                    class="flex-1 inline-flex justify-center items-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#4d148c] hover:bg-[#6d2ba8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4d148c]"
                    :disabled="isItemRequested(item)"
                    :class="{ 'opacity-50 cursor-not-allowed': isItemRequested(item) }">
                    <i class="fas fa-hand-holding-box mr-2"></i>
                    {{ getRequestButtonText(item) }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Updated Pagination -->
          <div v-if="items.length > 0" class="mt-8 flex justify-center">
            <div class="bg-white rounded-xl shadow-sm p-2 inline-flex items-center space-x-4">
              <button @click="changePage(pagination.current_page - 1)" :disabled="pagination.current_page === 1"
                class="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4d148c]">
                <i class="fas fa-chevron-left mr-1"></i>
                Previous
              </button>

              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-500">Page</span>
                <div class="px-3 py-1 rounded-lg bg-[#4d148c] text-white text-sm font-medium min-w-[2rem] text-center">
                  {{ pagination.current_page }}
                </div>
                <span class="text-sm text-gray-500">of {{ pagination.last_page }}</span>
              </div>

              <button @click="changePage(pagination.current_page + 1)"
                :disabled="pagination.current_page === pagination.last_page"
                class="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4d148c]">
                Next
                <i class="fas fa-chevron-right ml-1"></i>
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="items.length === 0" class="bg-white rounded-xl shadow-sm p-8 text-center">
            <div class="inline-block p-4 rounded-full bg-gray-100 mb-4">
              <i class="fas fa-box-open text-4xl text-gray-400"></i>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">No Items Available</h3>
            <p class="text-gray-500">Try adjusting your filters or check back later</p>
          </div>
        </div>
      </div>

      <!-- Request Modal -->
      <div v-if="showRequestModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title"
        role="dialog" aria-modal="true">
        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <!-- Background overlay -->
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeRequestModal"></div>

          <!-- Modal panel -->
          <div
            class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
            <div class="absolute top-0 right-0 pt-4 pr-4">
              <button @click="closeRequestModal" class="text-gray-400 hover:text-gray-500 focus:outline-none">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <div class="sm:flex sm:items-start">
              <div
                class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 sm:mx-0 sm:h-10 sm:w-10">
                <i class="fas fa-file-upload text-[#4d148c]"></i>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Upload Travel Document
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    Please upload a document proving your travel plans (e.g., ticket, booking confirmation)
                  </p>
                </div>
              </div>
            </div>

            <!-- Upload Section -->
            <div class="mt-5">
              <div class="space-y-4">
                <div class="flex items-center justify-center">
                  <label
                    class="relative cursor-pointer bg-white rounded-lg border-2 border-dashed border-gray-300 p-6 hover:border-[#4d148c] transition-colors">
                    <input type="file" class="hidden" accept=".pdf,.jpg,.jpeg,.png" @change="handleFileUpload">
                    <div class="space-y-2 text-center">
                      <i class="fas fa-cloud-upload-alt text-3xl text-gray-400"></i>
                      <div class="text-sm text-gray-600">
                        <span class="text-[#4d148c] font-medium">Click to upload</span> or drag and drop
                      </div>
                      <p class="text-xs text-gray-500">
                        PDF, JPEG, PNG up to 2MB
                      </p>
                    </div>
                  </label>
                </div>

                <!-- Selected File -->
                <div v-if="selectedFile" class="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div class="flex items-center space-x-2">
                    <i class="fas fa-file-alt text-[#4d148c]"></i>
                    <span class="text-sm text-gray-700">{{ selectedFile.name }}</span>
                  </div>
                  <button @click="selectedFile = null" class="text-gray-400 hover:text-gray-500">
                    <i class="fas fa-times"></i>
                  </button>
                </div>

                <!-- Error Message -->
                <div v-if="fileError" class="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                  <i class="fas fa-exclamation-circle mr-1"></i>
                  {{ fileError }}
                </div>
              </div>
            </div>

            <!-- Pricing & Offer Section -->
            <div class="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <i class="fas fa-dollar-sign text-[#4d148c] mr-2"></i>
                Pricing & Offer
              </h3>

              <!-- Sender's Offered Price -->
              <p class="text-gray-700 mb-2">
                <strong>Sender's Offered Price:</strong> ${{ selectedItemId.sender_price }}
              </p>

              <!-- Show offer input toggle -->
              <div v-if="!showOfferInput">
                <button @click="showOfferInput = true"
                  class="mt-2 px-4 py-2 bg-[#4d148c] text-white rounded hover:bg-[#3a0f6c] transition">
                  Offer Your Own Price
                </button>
              </div>

              <!-- Offer Input -->
              <div v-else class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Your Offer (USD)</label>
                <div class="relative">
                  <input v-model.number="deliverer_offer_price" type="number" min="0" step="0.01"
                    @input="validatePositiveNumber($event, 'deliverer_offer_price')"
                    @keypress="allowOnlyMoneyInput($event)"
                    class="w-full px-4 py-2 border rounded-md bg-white focus:ring-2 focus:ring-[#4d148c] focus:outline-none"
                    placeholder="Enter your offer amount" />
                  <i class="fas fa-coins absolute right-3 top-3 text-gray-400"></i>
                </div>
              </div>
            </div>


            <!-- Action Buttons -->
            <div class="mt-6 sm:flex sm:flex-row-reverse">
              <button @click="requestDelivery"
                class="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-[#4d148c] hover:bg-[#6d2ba8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4d148c] sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!selectedFile || loading">
                <i class="fas fa-paper-plane mr-2"></i>
                {{ loading ? 'Requesting...' : 'Submit Request' }}
              </button>
              <button @click="closeRequestModal"
                class="mt-3 w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4d148c] sm:mt-0 sm:w-auto">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConsentDialog :visible="showConsent" :role="currentRole" :terms="filteredTermsDialog"
        @accept="handleConsentAccepted" @cancel="showConsent = false" />
    </div>
  </div>
</template>

<script>
import DelivererSidebar from '@/components/DelivererSidebar.vue';
import api from '../services/api';
import debounce from 'lodash/debounce';
import ConsentDialog from '@/components/ConsentDialog.vue';
import { useToast } from 'vue-toastification';
import MapLocation from '@/components/MapLocation.vue';
export default {
  components: {
    DelivererSidebar,
    ConsentDialog,
    MapLocation
  },
  name: 'AvailableItems',
  data() {
    return {
      items: [],
      loading: false,
      error: null,
      filters: {
        search: '',
        location: '',
        date: '',
        per_page: 10
      },
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
      },
      locations: [
        'New York, USA',
        'London, UK',
        'Paris, France',
        'Tokyo, Japan',
        'Dubai, UAE'
      ],
      navigation: [
        { name: 'Dashboard', path: '/deliverer-dashboard', icon: 'fas fa-chart-line' },
        { name: 'Available Items', path: '/available-items', icon: 'fas fa-box-open' },
        { name: 'My Deliveries', path: '/my-deliveries', icon: 'fas fa-shipping-fast' },
        { name: 'Rejected Requests', path: '/rejected-requests', icon: 'fas fa-times' }
      ],
      userName: '',
      userInitials: '',
      showErrorPopup: false,
      errorMessage: '',
      errorPopupTimeout: null,
      showRequestModal: false,
      selectedFile: null,
      fileError: null,
      selectedItemId: null,
      //Dilaog
      showConsent: false,
      currentRole: 'deliverer',
      terms: [],

      //User location 
      location: null,
      locationError: null,

      radius_km: '',
      origin_address: null,
      destination_address: null,
      showMap: false,
      showOriginMap: false,
      showOfferInput: false,
      deliverer_offer_price: null,
      sidebarOpen: false

    }
  },
  created() {
    this.setUserInfo();
    this.loadItems(undefined, true);
    this.debouncedLoadItems = debounce(this.loadItems, 500);
  },
  computed: {
    filteredTermsDialog() {
      return this.terms.filter(t => t.role === this.currentRole);
    }
  },
  watch: {
    radius_km: {
      handler() {
        if (this.destination_address, this.origin_address) {
          this.loadItems(1, true);
        }
      },
      immediate: false
    },
    destination_address(newVal, oldVal) {
      if (newVal && this.origin_address && newVal !== oldVal) {
        this.loadItems(1, true);
      }
    },
    origin_address(newVal, oldVal) {
      if (newVal && this.destination_address && newVal !== oldVal) {
        this.loadItems(1, true);
      }
    }
  },
  mounted() {
    this.fetchTermsAndConditions();
    // Poll every 30 seconds, but only if tab is active
    this.intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.loadItems()
      }
    }, 30000)

    // Listen to tab visibility change
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeUnmount() {
    if (this.debouncedLoadItems) {
      this.debouncedLoadItems.cancel();
    }
    // Clean up
    clearInterval(this.intervalId)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    fetchTermsAndConditions() {
      const toast = useToast();
      api.get('api/user-consent')
        .then(response => {
          this.terms = response.data.data;
        })
        .catch(error => {
          console.error("Error fetching terms:", error);
          toast.error("Failed to fetch terms.");
        });
    },
    handleConsentAccepted() {
      this.showConsent = false;
      this.showRequestModal = true;
    },
    setUserInfo() {
      const user = JSON.parse(localStorage.getItem('user'));
      this.userName = user?.name || 'Demo User';
      this.userInitials = user?.name
        ? user.name
          .split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()
        : 'DU';
    },
    async loadItems(page = 1, showLoading) {
      if (!this.destination_address || !this.origin_address) {
        this.items = [];
        this.pagination = {
          current_page: 1,
          per_page: 10,
          total: 0,
          last_page: 1
        };
        return;
      }

      try {
        if (showLoading) {
          this.loading = true;
        }
        this.error = null;

        const queryParams = {
          page,
          per_page: this.filters.per_page
        };

        if (this.filters.search?.trim()) {
          queryParams.search = this.filters.search.trim();
        }
        if (this.filters.location) {
          queryParams.location = this.filters.location;
        }
        if (this.filters.date) {
          queryParams.date = this.filters.date;
        }

        const response = await api.post('/api/available-items',
          {
            radius_km: this.radius_km != '' ? this.radius_km : '10',  // Your request body
            origin_address: this.origin_address,
            destination_address: this.destination_address
          },
          {
            params: queryParams
          });

        if (response.data.success) {
          this.items = response.data.data.items;
          this.pagination = response.data.data.pagination;
        } else {
          throw new Error(response.data.message || 'Failed to load items');
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to load available items';
        console.error('Error loading items:', error);
      } finally {
        if (showLoading) {
          this.loading = false;
        }
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    getStatusClass(status) {
      const classes = {
        pending: 'bg-yellow-50 text-yellow-700',
        accepted: 'bg-green-50 text-green-700',
        rejected: 'bg-red-50 text-red-700',
        completed: 'bg-blue-50 text-blue-700'
      };
      return classes[status] || 'bg-gray-50 text-gray-700';
    },
    isItemRequested(item) {
      const userId = JSON.parse(localStorage.getItem('user'))?.id;
      return (item.requested_by && Array.isArray(item.requested_by) &&
        item.requested_by.includes(userId)) ||
        item.request_status === 'accepted';
    },
    getRequestButtonText(item) {
      if (this.isItemRequested(item)) {
        return 'Requested';
      }
      return 'Request to Deliver';
    },
    openRequestModal(itemId) {
      this.selectedItemId = itemId;
      this.selectedFile = null;
      this.fileError = null;
      this.showConsent = true
    },
    closeRequestModal() {
      this.showRequestModal = false;
      this.selectedItemId = null;
      this.selectedFile = null;
      this.fileError = null;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      this.fileError = null;

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        this.fileError = 'Invalid file type. Please upload a PDF, JPEG, or PNG file.';
        this.selectedFile = null;
        return;
      }

      // Validate file size (2MB = 2 * 1024 * 1024 bytes)
      if (file.size > 2 * 1024 * 1024) {
        this.fileError = 'File size must be less than 2MB';
        this.selectedFile = null;
        return;
      }

      this.selectedFile = file;
    },
    async requestDelivery() {
      if (!this.selectedFile) {
        this.fileError = 'Please upload a proof of travel document';
        return;
      }

      try {
        this.loading = true;

        // Create form data
        const formData = new FormData();
        formData.append('item_id', this.selectedItemId.id);
        formData.append('proof_of_travel', this.selectedFile);
        formData.append(' accept_privacy', true,);

        if (this.deliverer_offer_price !== null && this.deliverer_offer_price >= 0) {
          formData.append('deliverer_price', this.deliverer_offer_price);
        } else {
          formData.append('deliverer_price', this.selectedItemId.sender_price); // Default to sender's price if not set
        }

        const response = await api.post('/api/request-item', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          this.closeRequestModal();
          await this.loadItems(this.pagination.current_page, true);
        }
      } catch (error) {
        this.closeRequestModal();
        this.errorMessage = error.response?.data?.message || error.message || 'Failed to request item';
        this.showErrorPopup = true;

        if (this.errorPopupTimeout) {
          clearTimeout(this.errorPopupTimeout);
        }

        this.errorPopupTimeout = setTimeout(() => {
          this.showErrorPopup = false;
          this.errorMessage = '';
        }, 3000);

        console.error('Error requesting item:', error);
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      if (this.filters.search) {
        this.debouncedLoadItems(1);
      } else {
        this.loadItems(1, true);
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.loadItems(page, true);
      }
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.loadItems()
      }
    },

    getDestinationLocation(location) {
      if (location && typeof location === 'object' && location.lat && location.lon) {

        this.destination_address = {
          lat: Number(location.lat),
          lng: Number(location.lon),
        };
      } else if (typeof location === "string") {
        // Handle manual string entry
        this.destination_address = location;
      }
    },
    getOriginLocation(location) {
      if (location && typeof location === 'object' && location.lat && location.lon) {

        this.origin_address = {
          lat: Number(location.lat),
          lng: Number(location.lon),
        };
      } else if (typeof location === "string") {
        // Handle manual string entry
        this.origin_address = location;
      }
    },
    validatePositiveNumber(event, field) {
      const value = event.target.value;

      if (value === '' || value === null) {
        this[field] = null;
        return;
      }

      const number = parseFloat(value);
      if (isNaN(number) || number < 0) {
        this[field] = null;
        return;
      }

      // Limit to 2 decimal places
      this[field] = Math.floor(number * 100) / 100;
    },

    allowOnlyMoneyInput(event) {
      const key = event.key;
      if (['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(key)) return;

      const currentValue = event.target.value;
      const futureValue = currentValue + key;
      const regex = /^\d*\.?\d{0,2}$/;

      if (!regex.test(futureValue)) {
        event.preventDefault();
      }
    },
  },


}
</script>

<style scoped>
/* Reuse existing animations from the project */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-100px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.animate-slideInLeft {
  animation: slideInLeft 0.6s ease-out forwards;
}

.animate-slideInDown {
  animation: slideInDown 0.6s ease-out forwards;
}

.animate-fadeIn {
  animation: fadeIn 0.6s ease-out forwards;
}

/* Add smooth scrolling */
.overflow-auto {
  scroll-behavior: smooth;
}

/* Responsive image container */
.aspect-w-16 {
  position: relative;
  padding-bottom: 56.25%;
}

.aspect-w-16 img {
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  object-fit: cover;
}
</style>